/**
 * Logger Utility for BOPIS BillBerry
 *
 * This logger captures errors from:
 * - API calls
 * - React component rendering errors
 * - JavaScript runtime errors
 * - Unhandled promise rejections
 *
 * Logs are stored in localStorage with date-wise organization
 * and can be downloaded as files or sent to a backend server.
 */

// Log levels
const LOG_LEVELS = {
    ERROR: 'ERROR',
    WARN: 'WARN',
    INFO: 'INFO',
    DEBUG: 'DEBUG'
};

// Storage key prefix
const LOG_STORAGE_KEY = 'bopis_error_logs';

// Patterns to ignore (external sources not from our project)
const IGNORED_SOURCES = [
    'chrome-extension://',
    'moz-extension://',
    'safari-extension://',
    'edge-extension://',
    'extensions/',
    'webkit-masked-url://',
    'about:',
    'blob:chrome-extension',
    'blob:moz-extension',
];

/**
 * Check if the error source should be ignored (external sources like browser extensions)
 * @param {string} source - The source/filename of the error
 * @returns {boolean} - True if should be ignored
 */
const shouldIgnoreError = (source) => {
    if (!source) return false;

    const sourceString = String(source).toLowerCase();

    // Check if source matches any ignored patterns
    return IGNORED_SOURCES.some(pattern => sourceString.includes(pattern.toLowerCase()));
};

/**
 * Get current date in YYYY-MM-DD format for log file naming
 */
const getCurrentDateKey = () => {
    const now = new Date();
    return now.toISOString().split('T')[0]; // Returns YYYY-MM-DD
};

/**
 * Get formatted timestamp for log entries
 */
const getTimestamp = () => {
    const now = new Date();
    return now.toLocaleString('en-IN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true
    });
};

/**
 * Get all logs from localStorage
 */
const getAllLogs = () => {
    try {
        const logs = localStorage.getItem(LOG_STORAGE_KEY);
        return logs ? JSON.parse(logs) : {};
    } catch (error) {
        console.error('Error reading logs from localStorage:', error);
        return {};
    }
};

/**
 * Save logs to localStorage
 */
const saveLogs = (logs) => {
    try {
        localStorage.setItem(LOG_STORAGE_KEY, JSON.stringify(logs));
    } catch (error) {
        console.error('Error saving logs to localStorage:', error);
        // If storage is full, remove old logs
        if (error.name === 'QuotaExceededError') {
            clearOldLogs();
            try {
                localStorage.setItem(LOG_STORAGE_KEY, JSON.stringify(logs));
            } catch (e) {
                console.error('Still unable to save logs after clearing old ones:', e);
            }
        }
    }
};

/**
 * Clear logs older than 7 days
 */
const clearOldLogs = () => {
    const logs = getAllLogs();
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
    const cutoffDate = sevenDaysAgo.toISOString().split('T')[0];

    const filteredLogs = {};
    Object.keys(logs).forEach(dateKey => {
        if (dateKey >= cutoffDate) {
            filteredLogs[dateKey] = logs[dateKey];
        }
    });

    saveLogs(filteredLogs);
    return filteredLogs;
};

/**
 * Main logging function
 * @param {string} level - Log level (ERROR, WARN, INFO, DEBUG)
 * @param {string} source - Source file/component where error occurred
 * @param {string} message - Error message
 * @param {Object} details - Additional error details
 */
const log = (level, source, message, details = {}) => {
    const dateKey = getCurrentDateKey();
    const timestamp = getTimestamp();

    const logEntry = {
        timestamp,
        level,
        source,
        message,
        details: {
            ...details,
            userAgent: navigator.userAgent,
            url: window.location.href,
            userId: localStorage.getItem('userData')
                ? JSON.parse(localStorage.getItem('userData'))?.userId || 'N/A'
                : 'N/A'
        }
    };

    // Get existing logs
    const logs = getAllLogs();

    // Initialize date array if not exists
    if (!logs[dateKey]) {
        logs[dateKey] = [];
    }

    // Add new log entry
    logs[dateKey].push(logEntry);

    // Save to localStorage
    saveLogs(logs);

    // Also log to console for development
    const consoleMethod = level === LOG_LEVELS.ERROR ? 'error'
        : level === LOG_LEVELS.WARN ? 'warn'
        : 'log';
    console[consoleMethod](`[${timestamp}] [${level}] [${source}]`, message, details);

    // Optionally send to backend server (uncomment if you have a logging endpoint)
    // sendLogToServer(logEntry);

    return logEntry;
};

/**
 * Log an error
 * @param {string} source - Source file/component
 * @param {string} message - Error message
 * @param {Object} details - Additional details (error object, stack trace, etc.)
 */
export const logError = (source, message, details = {}) => {
    return log(LOG_LEVELS.ERROR, source, message, details);
};

/**
 * Log a warning
 */
export const logWarning = (source, message, details = {}) => {
    return log(LOG_LEVELS.WARN, source, message, details);
};

/**
 * Log info
 */
export const logInfo = (source, message, details = {}) => {
    return log(LOG_LEVELS.INFO, source, message, details);
};

/**
 * Log debug info
 */
export const logDebug = (source, message, details = {}) => {
    return log(LOG_LEVELS.DEBUG, source, message, details);
};

/**
 * Log API error with detailed information
 * @param {string} endpoint - API endpoint that failed
 * @param {Error} error - Error object from axios/fetch
 * @param {string} componentName - Component that made the API call
 */
export const logApiError = (endpoint, error, componentName = 'Unknown') => {
    const errorDetails = {
        endpoint,
        method: error?.config?.method?.toUpperCase() || 'N/A',
        statusCode: error?.response?.status || 'N/A',
        statusText: error?.response?.statusText || 'N/A',
        responseData: error?.response?.data || null,
        requestData: error?.config?.data || null,
        requestParams: error?.config?.params || null,
        errorMessage: error?.message || 'Unknown error',
        stack: error?.stack || 'No stack trace available'
    };

    return logError(
        `API Call - ${componentName}`,
        `API request to ${endpoint} failed with status ${errorDetails.statusCode}`,
        errorDetails
    );
};

/**
 * Log React component render error
 * @param {Error} error - Error object
 * @param {Object} errorInfo - React error info with componentStack
 * @param {string} componentName - Component where error occurred
 */
export const logRenderError = (error, errorInfo, componentName = 'Unknown Component') => {
    const errorDetails = {
        errorMessage: error?.message || 'Unknown render error',
        stack: error?.stack || 'No stack trace',
        componentStack: errorInfo?.componentStack || 'No component stack',
        errorType: 'React Render Error'
    };

    return logError(
        `React Component - ${componentName}`,
        `Component render failed: ${error?.message}`,
        errorDetails
    );
};

/**
 * Log JavaScript runtime error
 * @param {ErrorEvent} event - Error event from window.onerror
 */
export const logRuntimeError = (event) => {
    const filename = event.filename || 'Unknown file';

    // Ignore errors from browser extensions and external sources
    if (shouldIgnoreError(filename)) {
        console.log('[Logger] Ignored external error from:', filename);
        return null;
    }

    const errorDetails = {
        message: event.message || 'Unknown runtime error',
        filename: filename,
        lineno: event.lineno || 'N/A',
        colno: event.colno || 'N/A',
        stack: event.error?.stack || 'No stack trace',
        errorType: 'JavaScript Runtime Error'
    };

    return logError(
        `Runtime Error - ${errorDetails.filename}:${errorDetails.lineno}:${errorDetails.colno}`,
        errorDetails.message,
        errorDetails
    );
};

/**
 * Log unhandled promise rejection
 * @param {PromiseRejectionEvent} event - Unhandled rejection event
 */
export const logUnhandledRejection = (event) => {
    const reason = event.reason;
    const stack = reason?.stack || 'No stack trace';

    // Ignore errors from browser extensions and external sources
    if (shouldIgnoreError(stack)) {
        console.log('[Logger] Ignored external promise rejection');
        return null;
    }

    const errorDetails = {
        reason: reason?.message || String(reason) || 'Unknown rejection reason',
        stack: stack,
        errorType: 'Unhandled Promise Rejection'
    };

    return logError(
        'Unhandled Promise Rejection',
        errorDetails.reason,
        errorDetails
    );
};

/**
 * Download logs for a specific date as a text file
 * @param {string} dateKey - Date in YYYY-MM-DD format (optional, defaults to today)
 */
export const downloadLogs = (dateKey = null) => {
    const targetDate = dateKey || getCurrentDateKey();
    const logs = getAllLogs();
    const dateLogs = logs[targetDate];

    if (!dateLogs || dateLogs.length === 0) {
        console.warn(`No logs found for date: ${targetDate}`);
        alert(`No error logs found for ${targetDate}`);
        return;
    }

    // Format logs as readable text
    let logContent = `===========================================\n`;
    logContent += `BOPIS BillBerry - Error Log File\n`;
    logContent += `Date: ${targetDate}\n`;
    logContent += `Generated: ${getTimestamp()}\n`;
    logContent += `Total Entries: ${dateLogs.length}\n`;
    logContent += `===========================================\n\n`;

    dateLogs.forEach((entry, index) => {
        logContent += `--- Entry #${index + 1} ---\n`;
        logContent += `Timestamp: ${entry.timestamp}\n`;
        logContent += `Level: ${entry.level}\n`;
        logContent += `Source: ${entry.source}\n`;
        logContent += `Message: ${entry.message}\n`;
        logContent += `Details:\n${JSON.stringify(entry.details, null, 2)}\n`;
        logContent += `\n`;
    });

    // Create and download file
    const blob = new Blob([logContent], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `error_log_${targetDate}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
};

/**
 * Download all logs as a single file
 */
export const downloadAllLogs = () => {
    const logs = getAllLogs();
    const dateKeys = Object.keys(logs).sort();

    if (dateKeys.length === 0) {
        console.warn('No logs found');
        alert('No error logs found');
        return;
    }

    let logContent = `===========================================\n`;
    logContent += `BOPIS BillBerry - Complete Error Log File\n`;
    logContent += `Generated: ${getTimestamp()}\n`;
    logContent += `Date Range: ${dateKeys[0]} to ${dateKeys[dateKeys.length - 1]}\n`;
    logContent += `===========================================\n\n`;

    dateKeys.forEach(dateKey => {
        const dateLogs = logs[dateKey];
        logContent += `\n######## ${dateKey} (${dateLogs.length} entries) ########\n\n`;

        dateLogs.forEach((entry, index) => {
            logContent += `--- Entry #${index + 1} ---\n`;
            logContent += `Timestamp: ${entry.timestamp}\n`;
            logContent += `Level: ${entry.level}\n`;
            logContent += `Source: ${entry.source}\n`;
            logContent += `Message: ${entry.message}\n`;
            logContent += `Details:\n${JSON.stringify(entry.details, null, 2)}\n`;
            logContent += `\n`;
        });
    });

    // Create and download file
    const blob = new Blob([logContent], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `error_log_all_${getCurrentDateKey()}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
};

/**
 * Get logs for display (returns logs for a specific date or all)
 * @param {string} dateKey - Optional date filter
 */
export const getLogs = (dateKey = null) => {
    const logs = getAllLogs();
    if (dateKey) {
        return logs[dateKey] || [];
    }
    return logs;
};

/**
 * Get log summary (count of errors per date)
 */
export const getLogSummary = () => {
    const logs = getAllLogs();
    const summary = {};
    Object.keys(logs).forEach(dateKey => {
        summary[dateKey] = logs[dateKey].length;
    });
    return summary;
};

/**
 * Clear all logs
 */
export const clearAllLogs = () => {
    localStorage.removeItem(LOG_STORAGE_KEY);
    console.log('All logs cleared');
};

/**
 * Clear logs for a specific date
 * @param {string} dateKey - Date in YYYY-MM-DD format
 */
export const clearLogsForDate = (dateKey) => {
    const logs = getAllLogs();
    if (logs[dateKey]) {
        delete logs[dateKey];
        saveLogs(logs);
        console.log(`Logs cleared for ${dateKey}`);
    }
};

/**
 * Send log to backend server (optional - enable if you have a logging API)
 * @param {Object} logEntry - Log entry to send
 */
export const sendLogToServer = async (logEntry) => {
    try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_BASEURL}/api/v1/clientlog`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(logEntry)
        });

        if (!response.ok) {
            console.warn('Failed to send log to server');
        }
    } catch (error) {
        // Silently fail - we don't want logging to break the app
        console.warn('Could not send log to server:', error.message);
    }
};

/**
 * Initialize global error handlers
 * Call this once in your app's entry point (index.js or App.js)
 */
export const initializeErrorHandlers = () => {
    // Handle uncaught JavaScript errors
    window.onerror = (message, source, lineno, colno, error) => {
        logRuntimeError({
            message,
            filename: source,
            lineno,
            colno,
            error
        });
        // Return false to allow default browser error handling
        return false;
    };

    // Handle unhandled promise rejections
    window.onunhandledrejection = (event) => {
        logUnhandledRejection(event);
    };

    console.log('[Logger] Global error handlers initialized');
};

// Export default object with all functions
export default {
    logError,
    logWarning,
    logInfo,
    logDebug,
    logApiError,
    logRenderError,
    logRuntimeError,
    logUnhandledRejection,
    downloadLogs,
    downloadAllLogs,
    getLogs,
    getLogSummary,
    clearAllLogs,
    clearLogsForDate,
    sendLogToServer,
    initializeErrorHandlers,
    LOG_LEVELS
};
